// hooks/useFetchTemplateWithAssignment.ts
import { useCallback, useEffect, useState } from 'react';
import { getAPI } from '../../services/api.service';
import { toast } from 'react-toastify';

export type AssignmentItem = {
  _id: string;
  templateId?: string;
  templateSnapshot?: any;
  title?: string;
  group?: { id: string; title: string } | string;
  groupId?: string;
  assignedBy?: string;
  assignedAt?: string;
  dueAt?: string;
  visibleFrom?: string;
  visibleTo?: string;
  allowDrafts?: boolean;
  allowDuringNotes?: boolean;
  status?: string;
};

export type TemplateWithAssignments = {
  _id: string;
  title: string;
  description?: string;
  createdBy?: any;
  sections?: any;
  tags?: string[];
  assignments?: AssignmentItem[];
};

export const useFetchTemplateWithAssignment = (templateId?: string) => {
  const [data, setData] = useState<TemplateWithAssignments | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplate = useCallback(async (id?: string) => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Call backend endpoint — adjust path if your backend uses different route
      // I expect API to support /book-review-templates/with-assignments?templateId=... or /book-review-templates/:id?includeAssignments=true
      const res = await getAPI<{data:any,template:any}>(`book-review/single/template`, { templateId: id });
      // Flexible parsing depending on backend shape
      const payload = res?.data;
      if (payload?.data) {
        // shape like { status:200, data: [ ... ], total }
        // if data is array we expect single match
        if (Array.isArray(payload.data)) {
          setData(payload.data[0] ?? null);
        } else {
          setData(payload.data ?? null);
        }
      } else if (payload?.template) {
        setData(payload.template);
      } else {
        // fallback: raw object
        setData(payload as any);
      }
    } catch (err: any) {
      console.error('fetch template error', err);
      setError(err?.message || 'Failed to fetch template');
      toast.error(err?.message || 'Failed to fetch template');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (templateId) fetchTemplate(templateId);
  }, [templateId, fetchTemplate]);

  return { template: data, loading, error, refetch: fetchTemplate };
};
